/**
 * Ticket WK1: reine Logik der Wahlkalender-Grundlage.
 *
 * Bewusst ohne React, ohne Datenbank und ohne Dateizugriff: Spaltenlesung,
 * Zeilenprüfung, Namensnormalisierung und die Berechnung des Amtszeitendes
 * sind damit vollständig prüfbar. Das Ende der Amtszeit wird nirgends
 * gespeichert – es entsteht bei jedem Aufruf aus Amtsantritt und der
 * passenden Amtszeit-Zeile.
 */

import { BUNDESLAND_LABELS, type Bundesland } from "@/lib/contacts";

export { BUNDESLAND_LABELS };
export type { Bundesland };

/* ------------------------------------------------------------- Amtsart */

export const MAYOR_OFFICES = ["buergermeister", "oberbuergermeister", "beide"] as const;
export type MayorOffice = (typeof MAYOR_OFFICES)[number];

export const MAYOR_OFFICE_LABELS: Record<MayorOffice, string> = {
  buergermeister: "Bürgermeister",
  oberbuergermeister: "Oberbürgermeister",
  beide: "Bürgermeister und Oberbürgermeister",
};

/** Amtsbezeichnung der Quelle -> Amtsart. Unbekannte Schreibweise = null. */
export function officeFromLabel(label: string): MayorOffice | null {
  const v = label.trim().toLowerCase().replace(/\s+/gu, " ");
  if (v === "oberbürgermeister" || v === "oberbürgermeisterin") return "oberbuergermeister";
  if (v === "bürgermeister" || v === "bürgermeisterin") return "buergermeister";
  return null;
}

/**
 * Ticket WK1a: ausgeschriebener Bundeslandname der Quelle -> Schlüssel.
 * Bindestrich, Gedankenstrich und Leerzeichen gelten als gleichwertig
 * („Baden-Württemberg", „Baden Württemberg", „Baden – Württemberg"), eine
 * zusammengeschriebene Form wie „Nordrheinwestfalen" wird nicht geraten.
 */
export function bundeslandFromLabel(label: string): Bundesland | null {
  const norm = (value: string) =>
    value
      .trim()
      .toLowerCase()
      .replace(/[-–—_/]/gu, " ")
      .replace(/\s+/gu, " ")
      .trim();
  const wanted = norm(label);
  if (!wanted) return null;
  for (const key of Object.keys(BUNDESLAND_LABELS) as Bundesland[]) {
    if (norm(BUNDESLAND_LABELS[key]) === wanted) return key;
  }
  return null;
}


export const TERM_MODES = ["individuell", "gemeinsamer_wahltermin"] as const;
export type TermMode = (typeof TERM_MODES)[number];

export type TermLength = {
  bundesland: Bundesland;
  office: MayorOffice;
  years: number;
  validFrom: string;
  mode: TermMode;
};

/* ------------------------------------------------------- Amtszeitende */

export function isoDay(year: number, month: number, day: number): string {
  return `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

/**
 * Passende Amtszeit-Zeile: gleiche Amtsart oder die gemeinsame Zeile „beide",
 * gültig ab dem spätesten Beginn, der nicht nach dem Amtsantritt liegt.
 * Gibt es keine passende Zeile, ist die Rückgabe null – dann bleibt das Ende
 * leer statt geraten.
 */
export function findTermLength(
  terms: TermLength[],
  input: { bundesland: Bundesland; office: MayorOffice; startYear: number; startMonth: number },
): TermLength | null {
  const start = isoDay(input.startYear, input.startMonth, 1);
  const candidates = terms
    .filter(
      (t) =>
        t.bundesland === input.bundesland &&
        (t.office === input.office || t.office === "beide") &&
        t.validFrom <= start,
    )
    .sort((a, b) => {
      if (a.validFrom !== b.validFrom) return a.validFrom < b.validFrom ? 1 : -1;
      // Eine genau passende Amtsart schlägt die gemeinsame Zeile.
      return a.office === "beide" ? 1 : -1;
    });
  return candidates[0] ?? null;
}

/**
 * Ende der Amtszeit: Amtsantritt plus Dauer in Jahren. Ist der genaue Tag
 * bekannt, wird von ihm aus gerechnet, sonst vom Monatsersten.
 */
export function computeTermEnd(input: {
  startYear: number;
  startMonth: number;
  startDay?: number | null;
  years: number;
}): string {
  const day = input.startDay ?? 1;
  const endYear = input.startYear + input.years;
  const daysInMonth = new Date(Date.UTC(endYear, input.startMonth, 0)).getUTCDate();
  return isoDay(endYear, input.startMonth, Math.min(day, daysInMonth));
}

export const TERM_YEARS_MIN = 1;
export const TERM_YEARS_MAX = 12;

export type MunicipalityTermInput = {
  bundesland: Bundesland;
  office: MayorOffice;
  termStartYear: number;
  termStartMonth: number;
  termStartDay?: number | null;
  /** Ticket WK39: Amtszeit je Gemeinde; hat Vorrang vor der Landesregel. */
  termYears?: number | null;
};

/** Ende der Amtszeit einer Gemeinde oder null, wenn keine Amtszeit gepflegt ist. */
export function municipalityTermEnd(
  municipality: MunicipalityTermInput,
  terms: TermLength[],
): string | null {
  const own = municipality.termYears ?? null;
  const years =
    own !== null && Number.isInteger(own) && own >= TERM_YEARS_MIN && own <= TERM_YEARS_MAX
      ? own
      : findTermLength(terms, {
          bundesland: municipality.bundesland,
          office: municipality.office,
          startYear: municipality.termStartYear,
          startMonth: municipality.termStartMonth,
        })?.years ?? null;
  if (years === null) return null;
  return computeTermEnd({
    startYear: municipality.termStartYear,
    startMonth: municipality.termStartMonth,
    startDay: municipality.termStartDay ?? null,
    years,
  });
}

/* ------------------------------------------------------ Namensvergleich */

/**
 * Vergleichsform eines Gemeindenamens: Kleinschreibung, Umlaute aufgelöst,
 * Satzzeichen weg. Der Klammerzusatz bleibt erhalten, damit „Altheim" und
 * „Altheim (Alb)" nicht zusammenfallen.
 */
export function normalizeMunicipalityName(value: string): string {
  return value
    .trim()
    .toLowerCase()
    // „Landkreis Biberach" und „Biberach" bezeichnen denselben Kreis.
    .replace(/^landkreis\s+/u, "")
    .replace(/^stadtkreis\s+/u, "")
    // Amtliche Zusätze der Quelle: „Blaubeuren, Stadt" und „Bad Camberg,
    // Stadt, Kurstadt" sind Blaubeuren und Bad Camberg (Ticket WK40).
    .replace(/,.*$/u, "")
    .replace(/ä/gu, "ae")
    .replace(/ö/gu, "oe")
    .replace(/ü/gu, "ue")
    .replace(/ß/gu, "ss")
    .replace(/[().,]/gu, " ")
    .replace(/[-_/]/gu, " ")
    .replace(/\s+/gu, " ")
    .trim();
}

/** Vergleichsschlüssel einer Gemeinde: Landkreis und Name zusammen. */
export function municipalityKey(name: string, district: string): string {
  return `${normalizeMunicipalityName(district)}|${normalizeMunicipalityName(name)}`;
}

/**
 * Schlüssel, unter denen eine eingelesene Gemeinde im Bestand stehen kann.
 * Die Datenbank speichert „Verbandsgemeinde Aar-Einrich" als „Aar-Einrich"
 * (Ticket WK40), außer der kürzere Name gehört im selben Landkreis schon einer
 * anderen Gemeinde. Deshalb werden beide Formen gesucht.
 */
export function municipalityKeyCandidates(name: string, district: string): string[] {
  const keys = [municipalityKey(name, district)];
  const withoutPrefix = name.trim().replace(/^Verbandsgemeinde\s+/u, "");
  if (withoutPrefix && withoutPrefix !== name.trim())
    keys.push(municipalityKey(withoutPrefix, district));
  return keys;
}

/* ------------------------------------------------------------- Einlesen */

export const MUNICIPALITY_FIELDS = [
  "name",
  "bundesland",
  "district",
  "population",
  "officeLabel",
  "lastName",
  "firstName",
  "postalCode",
  "month",
  "year",
  "day",
  "termYears",
] as const;

export type MunicipalityField = (typeof MUNICIPALITY_FIELDS)[number];

/** Erlaubte Spaltenköpfe je Feld (Vergleich ohne Groß-/Kleinschreibung). */
export const MUNICIPALITY_COLUMN_ALIASES: Record<MunicipalityField, string[]> = {
  name: ["gemeinde", "gemeindename", "stadt", "ort"],
  bundesland: ["bundesland", "land"],
  district: ["landkreis", "kreis"],
  population: ["einwohner", "einwohnerzahl"],
  officeLabel: ["amt", "amtsbezeichnung"],
  lastName: ["name (inkl. titel)", "name", "nachname"],
  firstName: ["vorname"],
  postalCode: ["plz", "postleitzahl"],
  month: ["monat"],
  year: ["jahr"],
  day: ["tag"],
  termYears: ["amtszeit", "amtszeit (jahre)", "amtszeit in jahren", "wahlperiode"],
};

/**
 * Pflichtspalten: ohne sie ist die Datei nicht lesbar. Ticket WK1a: das
 * Bundesland steht je Zeile in der Datei und wird nicht mehr für den ganzen
 * Lauf gewählt – eine falsche Auswahl wäre ein stiller Fehler.
 */
export const MUNICIPALITY_REQUIRED_FIELDS: MunicipalityField[] = [
  "name",
  "bundesland",
  "district",
  "officeLabel",
  "month",
  "year",
];


function headerKey(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/gu, " ");
}

export type MunicipalityColumnMap = Partial<Record<MunicipalityField, number>>;

/**
 * Kopfzeile suchen. Die Vorlage trägt in der ersten Zeile Gruppenüberschriften
 * („Informationen zur Gemeinde"), erst die zweite Zeile nennt die Spalten.
 * Gesucht wird daher die erste Zeile, die „Gemeinde" und „Landkreis" enthält.
 */
export function findHeaderRow(matrix: string[][]): number {
  for (let i = 0; i < Math.min(matrix.length, 20); i += 1) {
    const cells = (matrix[i] ?? []).map(headerKey);
    if (cells.includes("gemeinde") && cells.some((c) => c === "landkreis" || c === "kreis")) return i;
  }
  return -1;
}

export function mapColumns(header: string[]): MunicipalityColumnMap {
  const map: MunicipalityColumnMap = {};
  header.forEach((raw, index) => {
    const key = headerKey(raw);
    if (!key) return;
    for (const field of MUNICIPALITY_FIELDS) {
      if (map[field] !== undefined) continue;
      if (MUNICIPALITY_COLUMN_ALIASES[field].includes(key)) {
        map[field] = index;
        return;
      }
    }
  });
  return map;
}

export type ParsedMunicipality = {
  rowNumber: number;
  name: string;
  bundesland: Bundesland;
  district: string;
  postalCode: string | null;
  population: number | null;
  officeLabel: string;
  office: MayorOffice;
  firstName: string | null;
  lastName: string | null;
  termStartMonth: number;
  termStartYear: number;
  termStartDay: number | null;
  /** Ticket WK39: leer, wenn die Datei keine Amtszeit nennt. */
  termYears?: number | null;
};


export type SkippedRow = { rowNumber: number; name: string; reason: string };

export type ParseResult = {
  rows: ParsedMunicipality[];
  skipped: SkippedRow[];
  duplicates: SkippedRow[];
  /** Fehlt eine Pflichtspalte, ist die Datei nicht lesbar. */
  missingColumns: MunicipalityField[];
};

function cell(row: string[], index: number | undefined): string {
  if (index === undefined) return "";
  return String(row[index] ?? "").trim();
}

function parseInteger(value: string): number | null {
  const cleaned = value.replace(/[.\s']/gu, "");
  if (!/^\d+$/u.test(cleaned)) return null;
  return Number(cleaned);
}

export const MUNICIPALITY_MAX_ROWS = 5000;

/**
 * Zeilenweise Prüfung. Eine fehlerhafte Zeile stoppt den Vorgang nicht,
 * sondern erscheint einzeln in `skipped`; mehrfach genannte Gemeinden
 * erscheinen in `duplicates` (die erste Nennung zählt).
 */
export function parseMunicipalitySheet(
  matrix: string[][],
  options: { maxYear?: number } = {},
): ParseResult {
  const headerIndex = findHeaderRow(matrix);
  if (headerIndex < 0) {
    return { rows: [], skipped: [], duplicates: [], missingColumns: MUNICIPALITY_REQUIRED_FIELDS };
  }
  const map = mapColumns(matrix[headerIndex] ?? []);
  const missingColumns = MUNICIPALITY_REQUIRED_FIELDS.filter((f) => map[f] === undefined);
  if (missingColumns.length > 0) return { rows: [], skipped: [], duplicates: [], missingColumns };

  const maxYear = options.maxYear ?? new Date().getUTCFullYear() + 1;
  const rows: ParsedMunicipality[] = [];
  const skipped: SkippedRow[] = [];
  const duplicates: SkippedRow[] = [];
  const seen = new Set<string>();

  for (let i = headerIndex + 1; i < matrix.length && i - headerIndex <= MUNICIPALITY_MAX_ROWS; i += 1) {
    const raw = matrix[i] ?? [];
    const rowNumber = i + 1;
    const name = cell(raw, map.name);
    const district = cell(raw, map.district);
    if (!name && !district && raw.every((c) => String(c ?? "").trim() === "")) continue;

    const add = (reason: string) => skipped.push({ rowNumber, name: name || "(ohne Gemeinde)", reason });

    if (!name) {
      add("Gemeinde fehlt");
      continue;
    }
    // Ticket WK1a: Bundesland je Zeile, ausgeschrieben. Fehlt es oder ist die
    // Schreibweise unbekannt, wird die Zeile übersprungen – kein Auffüllen mit
    // einem Standardwert, kein Rat.
    const bundeslandLabel = cell(raw, map.bundesland);
    const bundesland = bundeslandFromLabel(bundeslandLabel);
    if (!bundesland) {
      add(
        bundeslandLabel
          ? `Bundesland „${bundeslandLabel}" ist kein bekanntes Bundesland`
          : "Bundesland fehlt",
      );
      continue;
    }
    if (!district) {
      add("Landkreis fehlt");
      continue;
    }

    const officeLabel = cell(raw, map.officeLabel);
    const office = officeFromLabel(officeLabel);
    if (!office) {
      add(officeLabel ? `Amt „${officeLabel}" ist keine bekannte Amtsbezeichnung` : "Amt fehlt");
      continue;
    }
    const monthRaw = cell(raw, map.month);
    const month = parseInteger(monthRaw);
    if (month === null || month < 1 || month > 12) {
      add(monthRaw ? `Monat „${monthRaw}" ist kein Monat von 1 bis 12` : "Monat fehlt");
      continue;
    }
    const yearRaw = cell(raw, map.year);
    const year = parseInteger(yearRaw);
    if (year === null || year < 1990 || year > maxYear) {
      add(yearRaw ? `Jahr „${yearRaw}" liegt außerhalb von 1990 bis ${maxYear}` : "Jahr fehlt");
      continue;
    }

    const populationRaw = cell(raw, map.population);
    let population: number | null = null;
    if (populationRaw) {
      population = parseInteger(populationRaw);
      if (population === null) {
        add(`Einwohnerzahl „${populationRaw}" ist keine Zahl`);
        continue;
      }
    }

    const postalRaw = cell(raw, map.postalCode);
    let postalCode: string | null = null;
    if (postalRaw) {
      const digits = postalRaw.replace(/\s/gu, "");
      if (!/^\d{4,5}$/u.test(digits)) {
        add(`Postleitzahl „${postalRaw}" hat nicht fünf Ziffern`);
        continue;
      }
      postalCode = digits.padStart(5, "0");
    }

    const dayRaw = cell(raw, map.day);
    let termStartDay: number | null = null;
    if (dayRaw) {
      const day = parseInteger(dayRaw);
      const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
      if (day === null || day < 1 || day > daysInMonth) {
        add(`Tag „${dayRaw}" passt nicht zu Monat und Jahr`);
        continue;
      }
      termStartDay = day;
    }

    const termYearsRaw = cell(raw, map.termYears);
    let termYears: number | null = null;
    if (termYearsRaw) {
      termYears = parseInteger(termYearsRaw);
      if (termYears === null || termYears < TERM_YEARS_MIN || termYears > TERM_YEARS_MAX) {
        add(`Amtszeit „${termYearsRaw}" ist keine Jahreszahl von ${TERM_YEARS_MIN} bis ${TERM_YEARS_MAX}`);
        continue;
      }
    }

    // Ticket WK1a: dieselbe Gemeinde im selben Landkreis ist nur innerhalb
    // desselben Bundeslandes eine Dublette.
    const key = `${bundesland}|${municipalityKey(name, district)}`;
    if (seen.has(key)) {
      duplicates.push({ rowNumber, name, reason: `Gemeinde ist in der Datei mehrfach genannt (${district})` });
      continue;
    }
    seen.add(key);

    rows.push({
      rowNumber,
      name,
      bundesland,
      district,
      postalCode,
      population,
      officeLabel,
      office,
      firstName: cell(raw, map.firstName) || null,
      lastName: cell(raw, map.lastName) || null,
      termStartMonth: month,
      termStartYear: year,
      termStartDay,
      termYears,
    });

  }

  return { rows, skipped, duplicates, missingColumns: [] };
}

/* ------------------------------------------------ Namensangaben */

/** Vor- und Nachname aus einer zusammenhängenden Namensangabe. */
export function splitHolderName(value: string): { firstName: string; lastName: string } {
  const parts = value.trim().split(/\s+/u);
  if (parts.length === 1) return { firstName: "", lastName: parts[0] ?? "" };
  return { firstName: parts.slice(0, -1).join(" "), lastName: parts[parts.length - 1] ?? "" };
}

/* =========================================================== Ticket WK2 */

/* ------------------------------------------- Tritt erneut an (3 Zustände) */

export const RUNS_AGAIN_VALUES = ["unbekannt", "ja", "nein"] as const;
export type RunsAgain = (typeof RUNS_AGAIN_VALUES)[number];

export const RUNS_AGAIN_LABELS: Record<RunsAgain, string> = {
  unbekannt: "unbekannt",
  ja: "ja",
  nein: "nein",
};

/* ------------------------------------ Geltendes Wahldatum und Herkunft */

/** Woher das angezeigte Wahldatum stammt. */
export type ElectionDateSource = "berechnet" | "meldung";

export const ELECTION_SOURCE_LABELS: Record<ElectionDateSource, string> = {
  berechnet: "Errechnet aus Amtsantritt und Amtszeit",
  meldung: "Bestätigter Wahltermin aus einer Meldung",
};

export type EffectiveElectionInput = MunicipalityTermInput & {
  confirmedYear?: number | null;
  confirmedMonth?: number | null;
  confirmedDay?: number | null;
};

export type EffectiveElection = {
  /** ISO-Tag. Bei nur Monat und Jahr der Monatserste. */
  date: string;
  source: ElectionDateSource;
  /** Nur Monat und Jahr bekannt? Dann ist der Tag eine Annahme. */
  dayKnown: boolean;
};

/**
 * Ticket WK2, Schritt 2: Das geltende Wahldatum. Ein bestätigter Termin gilt,
 * das errechnete Datum tritt dann zurück. Ohne bestätigten Termin gilt das
 * Ende der Amtszeit.
 */
export function effectiveElectionDate(
  municipality: EffectiveElectionInput,
  terms: TermLength[],
): EffectiveElection | null {
  const year = municipality.confirmedYear ?? null;
  const month = municipality.confirmedMonth ?? null;
  if (year !== null && month !== null) {
    const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
    const day = municipality.confirmedDay ?? null;
    return {
      date: isoDay(year, month, day === null ? 1 : Math.min(day, daysInMonth)),
      source: "meldung",
      dayKnown: day !== null,
    };
  }
  const computed = municipalityTermEnd(municipality, terms);
  if (!computed) return null;
  return {
    date: computed,
    source: "berechnet",
    dayKnown: (municipality.termStartDay ?? null) !== null,
  };
}

/* ---------------------------------------------- Auslöser im Prüfeingang */

export const REVIEW_REASONS = ["kandidatur", "ergebnis", "presse", "wikidata", "kundenmeldung", "datenkorrektur"] as const;
export type ReviewReason = (typeof REVIEW_REASONS)[number];

export const REVIEW_REASON_LABELS: Record<ReviewReason, string> = {
  kandidatur: "Kandidatur steht an",
  ergebnis: "Ergebnis müsste vorliegen",
  presse: "Fund in der Presse",
  wikidata: "Befund aus Wikidata",
  kundenmeldung: "Meldung eines Kundenkontos",
  datenkorrektur: "Datenkorrektur",
};

/** Verständlicher Arbeitsauftrag statt einer technischen Fallbezeichnung. */
export const REVIEW_REASON_INSTRUCTIONS: Record<ReviewReason, string> = {
  kandidatur: "Bitte prüfen Sie, ob die amtierende Person erneut antritt, und ergänzen Sie die Angabe bei Bedarf.",
  ergebnis: "Bitte prüfen Sie das Wahlergebnis und tragen Sie die neue amtierende Person sowie den Amtsantritt ein.",
  presse: "Bitte prüfen Sie die Pressefunde auf eine relevante Änderung der Gemeindedaten.",
  wikidata: "Bitte vergleichen Sie unseren Datenstand mit dem Befund aus Wikidata.",
  kundenmeldung:
    "Ein Kundenkonto hat eine Änderung gemeldet. Bitte prüfen Sie die Angabe und übernehmen oder verwerfen Sie den Vorschlag.",
  datenkorrektur: "Die Redaktion hat die Gemeindedaten korrigiert. Alter und neuer Wert stehen im Fall.",
};

/* ------------------------------------------------ Ticket WK8: Quelle je Grund */

/**
 * Ticket WK8: Alle Fälle stehen an einer Stelle; je Eintrag bleibt die Quelle
 * erkennbar. Eine weitere Quelle reiht sich ein, indem sie hier einen Grund und
 * eine Quelle erhält – die Anzeige muss dafür nicht umgebaut werden.
 */
export const REVIEW_SOURCES = ["rechnung", "wikidata", "presse", "kundenmeldung", "redaktion"] as const;
export type ReviewSource = (typeof REVIEW_SOURCES)[number];

export const REVIEW_SOURCE_LABELS: Record<ReviewSource, string> = {
  rechnung: "Rechnung",
  wikidata: "Wikidata",
  presse: "Presse",
  kundenmeldung: "Kundenmeldung",
  redaktion: "Redaktion",
};

export const REVIEW_REASON_SOURCE: Record<ReviewReason, ReviewSource> = {
  kandidatur: "rechnung",
  ergebnis: "rechnung",
  presse: "presse",
  wikidata: "wikidata",
  kundenmeldung: "kundenmeldung",
  datenkorrektur: "redaktion",
};

export function reviewSourceOf(reason: ReviewReason): ReviewSource {
  return REVIEW_REASON_SOURCE[reason];
}

/** Die Quellen einer Kachel, ohne Wiederholung, in fester Reihenfolge. */
export function reviewSourcesOf(reasons: readonly ReviewReason[]): ReviewSource[] {
  return REVIEW_SOURCES.filter((source) => reasons.some((reason) => reviewSourceOf(reason) === source));
}

/* ------------------------------------- Ticket WK8: Vorschläge an einem Fall */

export const PROPOSAL_FIELDS = ["name", "einwohner", "landkreis", "wahldatum"] as const;
export type ProposalField = (typeof PROPOSAL_FIELDS)[number];

export const PROPOSAL_FIELD_LABELS: Record<ProposalField, string> = {
  name: "Name der amtierenden Person",
  einwohner: "Einwohnerzahl",
  landkreis: "Landkreis",
  wahldatum: "Wahldatum",
};

export type ReviewProposalEntry = {
  field: ProposalField;
  /** Unser heutiger Stand, als Text für die Anzeige. */
  oldValue: string | null;
  /** Der vorgeschlagene Wert. Null heißt: es gibt nichts zu übernehmen. */
  proposed: string | null;
  note: string;
};

/** Ein Vorschlag mit seiner serverseitig geprüften Herkunft. */
export type ReviewProposalItem = ReviewProposalEntry & {
  reviewId: string;
  entryIndex: number;
};

export function proposalExplanation(entry: ReviewProposalEntry): string {
  if (entry.proposed !== null) {
    return `${PROPOSAL_FIELD_LABELS[entry.field]}: Bei creategy steht „${entry.oldValue ?? "nicht eingetragen"}“. Wikidata schlägt „${entry.proposed}“ vor.`;
  }
  if (entry.note.includes("mehrere amtierende Personen")) {
    return "Wikidata nennt mehrere amtierende Personen. Deshalb kann keine Person eindeutig vorgeschlagen werden; bitte prüfen und bearbeiten Sie den Namen von Hand.";
  }
  return `${entry.note || "Die Quelle liefert keinen eindeutigen Wert."} Bitte prüfen Sie die Angabe von Hand.`;
}

export type ReviewProposal = {
  source: ReviewSource;
  entries: ReviewProposalEntry[];
};

/* Ticket WK23 (28.09.2026): Der Wikidata-Abgleich ist entfernt. Der Grund
 * „wikidata“ bleibt nur als Anzeige für abgeschlossene Altfälle erhalten. */

/**
 * Ticket WK8: Der Haken übernimmt genau den vorgeschlagenen Wert. Ein Eintrag
 * ohne Vorschlag ändert nichts.
 */
export function applyProposalEntry<
  T extends { firstName: string; lastName: string; population: string },
>(values: T, entry: ReviewProposalEntry): T {
  if (entry.proposed === null) return values;
  if (entry.field === "name") {
    const { firstName, lastName } = splitHolderName(entry.proposed);
    return { ...values, firstName, lastName };
  }
  return { ...values, population: entry.proposed };
}

export type ReviewTrigger = { reason: ReviewReason; referenceDate: string };


function addDays(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return isoDay(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate());
}

export function addMonths(iso: string, months: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  const year = d.getUTCFullYear();
  const month = d.getUTCMonth() + 1 + months;
  const targetYear = year + Math.floor((month - 1) / 12);
  const targetMonth = ((month - 1 + 12 * 1000) % 12) + 1;
  const daysInMonth = new Date(Date.UTC(targetYear, targetMonth, 0)).getUTCDate();
  return isoDay(targetYear, targetMonth, Math.min(d.getUTCDate(), daysInMonth));
}

/** Erster Tag des Folgemonats zu einem ISO-Tag. */
export function firstOfNextMonth(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  const month = d.getUTCMonth() + 2;
  const year = d.getUTCFullYear() + (month > 12 ? 1 : 0);
  return isoDay(year, month > 12 ? month - 12 : month, 1);
}

/**
 * Ticket WK2, Schritt 3: Welche Gründe treffen heute auf eine Gemeinde zu?
 *
 * „Kandidatur steht an": sechs Monate vor dem geltenden Wahldatum.
 * „Ergebnis müsste vorliegen": einen Tag nach dem geltenden Wahldatum; ist nur
 * Monat und Jahr bekannt, am ersten Tag des Folgemonats.
 * Beide rechnen gegen den geltenden Termin (bestätigt, sonst errechnet).
 */
export function reviewTriggersFor(
  municipality: EffectiveElectionInput,
  terms: TermLength[],
  today: string,
): ReviewTrigger[] {
  const effective = effectiveElectionDate(municipality, terms);
  if (!effective) return [];
  const triggers: ReviewTrigger[] = [];

  const candidacyFrom = addMonths(effective.date, -6);
  if (today >= candidacyFrom) triggers.push({ reason: "kandidatur", referenceDate: effective.date });

  const resultFrom = effective.dayKnown ? addDays(effective.date, 1) : firstOfNextMonth(effective.date);
  if (today >= resultFrom) triggers.push({ reason: "ergebnis", referenceDate: effective.date });

  return triggers;
}

/* ---------------------------------------------- Sortierung des Eingangs */

export type ReviewTile = {
  municipalityId: string;
  reasons: ReviewReason[];
  /** Geltendes Wahldatum als ISO-Tag. */
  electionDate: string;
};

/**
 * Überfällige Wahlen zuerst (Grund „Ergebnis müsste vorliegen"), danach die
 * bevorstehenden Kandidaturen; innerhalb einer Gruppe das älteste Datum zuerst.
 */
export function sortReviewTiles<T extends ReviewTile>(tiles: T[]): T[] {
  const rank = (tile: T) => (tile.reasons.includes("ergebnis") ? 0 : 1);
  return [...tiles].sort((a, b) => {
    const byGroup = rank(a) - rank(b);
    if (byGroup !== 0) return byGroup;
    if (a.electionDate !== b.electionDate) return a.electionDate < b.electionDate ? -1 : 1;
    return a.municipalityId < b.municipalityId ? -1 : 1;
  });
}

/**
 * Eine Gemeinde erzeugt immer nur eine Kachel, auch bei mehreren Gründen.
 * Die Gründe stehen dann nebeneinander auf derselben Kachel.
 */
export function groupReviewsByMunicipality<
  T extends { municipalityId: string; reason: ReviewReason; referenceDate: string },
>(reviews: T[]): Array<{ municipalityId: string; reasons: ReviewReason[]; electionDate: string; entries: T[] }> {
  const byMunicipality = new Map<string, T[]>();
  for (const review of reviews) {
    const list = byMunicipality.get(review.municipalityId) ?? [];
    list.push(review);
    byMunicipality.set(review.municipalityId, list);
  }
  return [...byMunicipality.entries()].map(([municipalityId, entries]) => ({
    municipalityId,
    reasons: REVIEW_REASONS.filter((reason) => entries.some((entry) => entry.reason === reason)),
    electionDate: entries.map((entry) => entry.referenceDate).sort()[0] ?? "",
    entries,
  }));
}

/* -------------------------------------------- Filter in der Gemeindeliste */

export const ELECTION_PERIODS = ["alle", "ueberfaellig", "6", "12", "24"] as const;
export type ElectionPeriod = (typeof ELECTION_PERIODS)[number];

export const ELECTION_PERIOD_LABELS: Record<ElectionPeriod, string> = {
  alle: "Alle Zeiträume",
  ueberfaellig: "Überfällig",
  "6": "Bis 6 Monate",
  "12": "Bis 12 Monate",
  "24": "Bis 24 Monate",
};

/** Trifft der Zeitraumfilter auf ein geltendes Wahldatum zu? */
export function matchesElectionPeriod(
  electionDate: string | null,
  period: ElectionPeriod,
  today: string,
): boolean {
  if (period === "alle") return true;
  if (!electionDate) return false;
  if (period === "ueberfaellig") return electionDate < today;
  if (electionDate < today) return false;
  return electionDate <= addMonths(today, Number(period));
}

/** Freitextsuche über Gemeindename und Name der amtierenden Person. */
export function matchesMunicipalitySearch(
  row: { name: string; firstName?: string | null; lastName?: string | null },
  search: string,
): boolean {
  const term = search.trim().toLowerCase();
  if (!term) return true;
  const haystack = [row.name, row.firstName ?? "", row.lastName ?? ""].join(" ").toLowerCase();
  return haystack.includes(term);
}

